import { calculateSkaterPoints, calculateGoaliePoints } from './points';
import countPoints from './countPoints';
import { POSITION_ARRAYS } from '../constants/positions';
import { applyDeltaToPlayer } from './playerStats';

/**
 * Parse live game data from the NHL scores API and produce per-player stat deltas
 * for pool participants only.
 *
 * @param {Array} games - Array of game objects from the NHL scores endpoint
 * @param {Set<number>} poolPlayerIds - Set of nhl_ids for all players in the pool
 * @returns {{ playerDeltas: Map<number, object>, hasLiveGames: boolean }}
 */
export const parseLivePlayerStats = (games, poolPlayerIds) => {
  const playerDeltas = new Map();

  if (!games || !Array.isArray(games) || !poolPlayerIds || poolPlayerIds.size === 0) {
    return { playerDeltas, hasLiveGames: false };
  }

  // NHL Web API gameState values include LIVE, CRIT (critical/sudden-death — common
  // throughout playoff overtime), OVER (brief post-final transition), FINAL, and OFF.
  // All of these may contain goal/assist data that should contribute to live deltas.
  const activeStates = new Set(['LIVE', 'CRIT', 'OVER', 'FINAL', 'OFF']);
  const liveStates = new Set(['LIVE', 'CRIT']);
  const activeGames = games.filter(g => activeStates.has(g.gameState));
  const hasLiveGames = games.some(g => liveStates.has(g.gameState));

  for (const game of activeGames) {
    const goals = game.goals;
    if (!Array.isArray(goals)) continue;

    for (const goal of goals) {
      const scorerId = goal.playerId;

      // Count goal for scorer (if in pool)
      if (poolPlayerIds.has(scorerId)) {
        const delta = getOrCreateDelta(playerDeltas, scorerId);
        delta.goals += 1;

        // Check for OT goal
        if (goal.periodDescriptor?.periodType === 'OT') {
          delta.otGoals += 1;
        }
      }

      // Count assists
      if (Array.isArray(goal.assists)) {
        for (const assist of goal.assists) {
          if (poolPlayerIds.has(assist.playerId)) {
            const delta = getOrCreateDelta(playerDeltas, assist.playerId);
            delta.assists += 1;
          }
        }
      }
    }
  }

  // Calculate points for each player delta
  for (const [, delta] of playerDeltas) {
    delta.points = calculateSkaterPoints(delta.goals, delta.assists, delta.otGoals);
  }

  return { playerDeltas, hasLiveGames };
};

/**
 * Build a Set of all pool player nhl_ids from the rosters array.
 *
 * @param {Array} rosters - Array of roster objects (with normalized players)
 * @returns {Set<number>}
 */
export const buildPoolPlayerIds = (rosters) => {
  const ids = new Set();
  if (!rosters || !Array.isArray(rosters)) return ids;

  for (const roster of rosters) {
    for (const pos of POSITION_ARRAYS) {
      if (Array.isArray(roster[pos])) {
        for (const player of roster[pos]) {
          if (player?.id) ids.add(player.id);
        }
      }
    }
    if (roster.utility?.id) ids.add(roster.utility.id);
  }

  return ids;
};

/**
 * Augment a single roster's players with live stat deltas.
 * Clones each player and attaches a _delta property with today's stats.
 * Augmented flat fields (goals, assists, etc.) include live additions.
 *
 * @param {object} roster - A roster object with position arrays and utility
 * @param {Map<number, object>} playerDeltas - Map of nhl_id → { goals, assists, otGoals, points }
 * @returns {object} - New roster with augmented player objects
 */
export const augmentRoster = (roster, playerDeltas) => {
  if (!roster || !playerDeltas || playerDeltas.size === 0) return roster;

  const augmentPlayer = (player) => {
    if (!player) return player;
    return applyDeltaToPlayer(player, playerDeltas.get(player.id));
  };

  const augmented = { ...roster };
  for (const pos of POSITION_ARRAYS) {
    if (Array.isArray(augmented[pos])) {
      augmented[pos] = augmented[pos].map(augmentPlayer);
    }
  }
  if (augmented.utility) {
    augmented.utility = augmentPlayer(augmented.utility);
  }

  // Recalculate roster-level points total from augmented players
  augmented.points = countPoints(augmented);

  return augmented;
};

/**
 * Augment a flat list of unselected players (from useUnselectedPlayers) with
 * live stat deltas. Unselected players use the same normalized stat shape as
 * roster players, so we share `applyDeltaToPlayer` here. The only quirk is
 * that unselected players store their NHL id as a string while live deltas are
 * keyed by numeric ids.
 *
 * @param {Array} unselectedPlayers - List from useUnselectedPlayers
 * @param {Map<number, object>} playerDeltas - Combined delta map
 * @returns {Array} - New list with augmented players (or the same list if no deltas apply)
 */
export const augmentUnselectedPlayers = (unselectedPlayers, playerDeltas) => {
  if (!Array.isArray(unselectedPlayers) || unselectedPlayers.length === 0) return unselectedPlayers;
  if (!playerDeltas || playerDeltas.size === 0) return unselectedPlayers;

  return unselectedPlayers.map(player => {
    if (!player) return player;
    const numericId = typeof player.id === 'string' ? parseInt(player.id, 10) : player.id;
    return applyDeltaToPlayer(player, playerDeltas.get(numericId));
  });
};

/**
 * Parse live goalie stats from NHL boxscore data and produce per-goalie stat deltas
 * for pool participants only.
 *
 * @param {Array} boxscores - Array of boxscore objects from the NHL gamecenter API
 * @param {Set<number>} poolPlayerIds - Set of nhl_ids for all players in the pool
 * @returns {Map<number, object>} - Map of goalie nhl_id → { wins, shutouts, otl, points }
 */
export const parseLiveGoalieStats = (boxscores, poolPlayerIds) => {
  const goalieDeltas = new Map();

  if (!boxscores || !Array.isArray(boxscores) || !poolPlayerIds || poolPlayerIds.size === 0) {
    return goalieDeltas;
  }

  const ACTIVE_GOALIE_STATES = new Set(['LIVE', 'CRIT', 'OVER', 'FINAL', 'OFF']);

  for (const boxscore of boxscores) {
    const gameState = boxscore.gameState;
    if (!ACTIVE_GOALIE_STATES.has(gameState)) continue;

    const lastPeriodType = boxscore.gameOutcome?.lastPeriodType; // 'REG', 'OT', 'SO'

    const sides = ['homeTeam', 'awayTeam'];
    for (const side of sides) {
      const goalies = boxscore.playerByGameStats?.[side]?.goalies;
      if (!Array.isArray(goalies)) continue;

      for (const goalie of goalies) {
        const goalieId = goalie.playerId;
        if (!poolPlayerIds.has(goalieId)) continue;

        // Only goalies with a decision have actionable stats
        if (!goalie.decision) continue;

        const delta = getOrCreateGoalieDelta(goalieDeltas, goalieId);

        if (goalie.decision === 'W') {
          delta.wins += 1;
          // Shutout requires the goalie to have played the entire game with 0 GA.
          // Prefer the official `starter` flag when the NHL provides it; fall back
          // to inferring "lone goalie" from TOI when `starter` is missing (the NHL
          // sometimes omits it for live/recently-finished games).
          if (goalie.goalsAgainst === 0 && isStartingGoalie(goalie, goalies)) {
            delta.shutouts += 1;
          }
        } else if (goalie.decision === 'L') {
          // OTL: loss in OT or SO (not regulation)
          if (lastPeriodType && lastPeriodType !== 'REG') {
            delta.otl += 1;
          }
        }
      }
    }
  }

  // Calculate points for each goalie delta
  for (const [, delta] of goalieDeltas) {
    delta.points = calculateGoaliePoints(delta.wins, delta.shutouts, delta.otl);
  }

  return goalieDeltas;
};

/**
 * Merge skater deltas and goalie deltas into a single playerDeltas Map.
 *
 * @param {Map<number, object>} skaterDeltas - From parseLivePlayerStats
 * @param {Map<number, object>} goalieDeltas - From parseLiveGoalieStats
 * @returns {Map<number, object>} - Combined delta map
 */
export const mergeLiveDeltas = (skaterDeltas, goalieDeltas) => {
  const merged = new Map(skaterDeltas);
  for (const [id, delta] of goalieDeltas) {
    merged.set(id, delta);
  }
  return merged;
};

// --- Helpers ---

// Convert a "MM:SS" or "HH:MM:SS" TOI string to total seconds. Returns 0 for
// missing/invalid values so we can safely compare across goalies.
const toiToSeconds = (toi) => {
  if (typeof toi !== 'string' || toi.length === 0) return 0;
  const parts = toi.split(':').map(Number);
  if (parts.some(Number.isNaN)) return 0;
  return parts.reduce((acc, n) => acc * 60 + n, 0);
};

// True when the given goalie is the only one on their side with any TOI.
const isLoneGoalie = (goalie, sideGoalies) => {
  if (toiToSeconds(goalie.toi) === 0) return false;
  return sideGoalies.every(g => g.playerId === goalie.playerId || toiToSeconds(g.toi) === 0);
};

// Treat a goalie as "the starter" when the NHL marks them as such; if the
// `starter` field is missing entirely from the boxscore, fall back to inferring
// it from TOI. An explicit `starter: false` is respected and not overridden.
const isStartingGoalie = (goalie, sideGoalies) => {
  if (goalie.starter === true) return true;
  if (goalie.starter === false) return false;
  return isLoneGoalie(goalie, sideGoalies);
};

const getOrCreateDelta = (map, playerId) => {
  if (!map.has(playerId)) {
    map.set(playerId, { goals: 0, assists: 0, otGoals: 0, wins: 0, shutouts: 0, otl: 0, points: 0 });
  }
  return map.get(playerId);
};

const getOrCreateGoalieDelta = (map, playerId) => {
  if (!map.has(playerId)) {
    map.set(playerId, { wins: 0, shutouts: 0, otl: 0, points: 0 });
  }
  return map.get(playerId);
};
